import {
    AppointmentReminder,
    AppointmentReminderDto,
    AppointmentReminderWithIdDto,
    AppointmentType,
    AppointmentTypeFilter,
    CreateAppointmentReminderRequest,
    GetAppointmentRemindersResponse,
    ReminderTemplateType,
} from "@accurx/api/appointment";
import { HttpError, httpClient2 } from "@accurx/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { API_PATHS } from "domains/appointment-reminders/constants";

import { remindersAndFilterOptionsQueryKey } from "./useRemindersAndFilterOptions";

type CreateReminderParameters = {
    workspaceId: string;
};

function mapLegacyRequestToRequest(
    legacyRequest: CreateAppointmentReminderRequest,
): AppointmentReminderDto {
    const appointmentTypeFilter =
        legacyRequest.appointmentTypeFilters as unknown as AppointmentType[];

    return {
        slotTypeFilter: legacyRequest.slotTypes,
        ...(legacyRequest.appointmentTypeFilters?.length
            ? { appointmentTypeFilter }
            : {}),
        ...(legacyRequest.siteNames.length
            ? { siteNameFilter: legacyRequest.siteNames }
            : {}),
        templateType: legacyRequest.type as ReminderTemplateType,
        customMessageBody: legacyRequest.customMessageBody,
        oneWeekEnabled: legacyRequest.oneWeekReminderEnabled ?? false,
        threeWorkingDayEnabled:
            legacyRequest.threeWorkingDayReminderEnabled ?? false,
        amendmentDeadline: legacyRequest.appointmentChangingDeadline,
        cancellation: legacyRequest.appointmentCancellationSupport,
        rebooking: legacyRequest.appointmentRebookingSupport,
        postAppointmentEnabled:
            legacyRequest.postAppointmentMessageEnabled ?? false,
        postAppointmentMessageBody: legacyRequest.postAppointmentCustomMessage,
    };
}

function mapReminderToLegacyReminder(
    reminder: AppointmentReminderWithIdDto,
): AppointmentReminder {
    const appointmentTypeFilters = reminder.appointmentTypeFilter
        ? (reminder.appointmentTypeFilter as unknown as AppointmentTypeFilter[])
        : [];

    return {
        id: reminder.id,
        slotTypes: reminder.slotTypeFilter,
        siteNames: reminder.siteNameFilter ?? [],
        appointmentTypeFilters,
        type: reminder.templateType,
        customMessageBody: reminder.customMessageBody,
        oneWeekReminderEnabled: reminder.oneWeekEnabled,
        threeWorkingDayReminderEnabled: reminder.threeWorkingDayEnabled,
        appointmentChangingDeadline: reminder.amendmentDeadline,
        appointmentCancellationSupport: reminder.cancellation,
        appointmentRebookingSupport: reminder.rebooking,
        postAppointmentMessageEnabled: reminder.postAppointmentEnabled,
        postAppointmentCustomMessage: reminder.postAppointmentMessageBody,
    };
}

export const createReminderQueryKey = (workspaceId: number | string) => [
    "AppointmentReminders",
    "CreateReminder",
    { workspaceId: `${workspaceId}` },
];

export const useCreateReminder = ({
    workspaceId,
}: CreateReminderParameters) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: createReminderQueryKey(workspaceId),
        mutationFn: async (
            legacyReminder: CreateAppointmentReminderRequest,
        ) => {
            try {
                return await httpClient2
                    .post({
                        path: API_PATHS.AppointmentReminders,
                        params: { workspaceId },
                        json: mapLegacyRequestToRequest(legacyReminder),
                    })
                    .json<AppointmentReminderWithIdDto>();
            } catch (error) {
                let message = "Could not create appointment reminder";
                if (error instanceof HttpError) {
                    type ExpectedErrorResponse =
                        | { message: string }
                        | undefined;
                    const errorResponse =
                        (await error.response.json()) as ExpectedErrorResponse;
                    if (errorResponse?.message) {
                        message = errorResponse.message;
                    }
                }

                throw new Error(message);
            }
        },
        onSuccess(data) {
            queryClient.setQueryData<GetAppointmentRemindersResponse>(
                remindersAndFilterOptionsQueryKey(workspaceId),
                (previousData) => {
                    const newReminder = mapReminderToLegacyReminder(data);

                    /**
                     * If no previous data, we still need to set the reminders
                     * list query data. This happens when creating the first
                     * reminder.
                     */
                    if (!previousData) {
                        return {
                            appointmentReminders: [newReminder],
                        } as unknown as GetAppointmentRemindersResponse;
                    }

                    return {
                        ...previousData,
                        appointmentReminders: [
                            ...previousData.appointmentReminders,
                            newReminder,
                        ],
                    };
                },
            );
        },
    });
};
